import React, { useState, useEffect } from 'react'
import useTranslate from '../hooks/useTranslate';
import { FaFacebookF, FaInstagram, FaYoutube, FaLinkedinIn, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaArrowRight } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { trackContact } from '../helpers/analyticsHelper';

const Footer = () => {
    const user = useSelector(state => state?.user?.user)

    const whatsappLink = (() => {
        const baseUrl = "https://wa.me/916266761336";
        let message = "Hello FasalShop Support, I have a query.";

        if (user?.id || user?._id) {
            const userName = user.first_name || user.last_name || "";
            const userPhone = user.mobile || user.phone || "";
            message = `Hello FasalShop Support, I am ${userName}, ${userPhone ? `Phone: ${userPhone}` : ""}. I have a query.`;
        }

        return `${baseUrl}?text=${encodeURIComponent(message)}`;
    })();
    const t = useTranslate({
        tagline: "Empowering Indian Farmers with Innovation and Technology. India's fastest growing full-stack AgriTech platform dedicated to revolutionizing the agricultural landscape.",
        marketplace: "Marketplace",
        quickLinks: "Quick Links",
        farmersHub: "Farmers Hub",
        getInTouch: "Get In Touch",
        callSupport: "Call Support",
        whatsappUs: "Whatsapp Us",
        ourOffice: "Our Office",
        officeAddress: "Coral wood D1 H No - 305, Narmadapuram Rd, Misrod, Bhopal, Madhya Pradesh 462047",
        copyright: "FasalShop Agro Private Limited. All Rights Reserved.",
        brandFirst: "Fasal",
        brandSecond: "Shop"
    });

    const marketplaceLinks = ['Pesticides', 'Fertilizers', 'Organic Farming', 'Agricultural Tools', 'Crop Protection', 'Seed Varieties'];
    const quickLinks = ['About FasalShop', 'Privacy Policy', 'Terms of Service', 'Support Center', 'FAQ Section', 'Sitemap'];
    const farmersHubLinks = ['Latest Mandi Rates', 'Weather Forecast', 'Soil Testing Services', 'Kisan Helpline', 'Agri Loan Info', 'Crop Insurance'];

    const [translatedMarketplace, setTranslatedMarketplace] = useState(marketplaceLinks);
    const [translatedQuickLinks, setTranslatedQuickLinks] = useState(quickLinks);
    const [translatedFarmersHub, setTranslatedFarmersHub] = useState(farmersHubLinks);

    const { languageCode, translateText } = React.useContext(require('../context').default);

    useEffect(() => {
        const translateAll = async () => {
            if (!languageCode || languageCode === 'en') {
                setTranslatedMarketplace(marketplaceLinks);
                setTranslatedQuickLinks(quickLinks);
                setTranslatedFarmersHub(farmersHubLinks);
                return;
            }
            try {
                const [m, q, f] = await Promise.all([
                    Promise.all(marketplaceLinks.map(l => translateText(l, languageCode))),
                    Promise.all(quickLinks.map(l => translateText(l, languageCode))),
                    Promise.all(farmersHubLinks.map(l => translateText(l, languageCode)))
                ]);
                setTranslatedMarketplace(m);
                setTranslatedQuickLinks(q);
                setTranslatedFarmersHub(f);
            } catch (error) {
                console.error("Footer translation error:", error);
            }
        };
        translateAll();
    }, [languageCode, translateText]);

    return (
        <footer className='bg-[#1c3026] text-gray-300 pt-20 pb-10 relative overflow-hidden'>
            {/* Subtle decorative background element */}
            <div className='absolute top-0 right-0 w-1/3 h-full bg-[#00b259]/5 blur-[120px] rounded-full pointer-events-none'></div>

            <div className='container mx-auto px-6 relative z-10'>

                {/* TOP SECTION: BRAND & TAGLINE */}
                <div className='mb-16 border-b  pb-12'>
                    <div className='mb-6'>
                        <img
                            src='/fasalshop.png'
                            alt='FasalShop'
                            className='h-20 w-auto object-contain'
                        />
                    </div>
                    <p className='text-sm leading-relaxed text-gray-400 font-medium max-w-2xl opacity-80'>
                        {t.tagline}
                    </p>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8'>

                    {/* Column 1: About */}
                    <div className='flex flex-col gap-8'>
                        <h4 className='text-white font-black text-sm uppercase tracking-[3px] flex items-center gap-3'>
                            <div className='w-2 h-2 bg-[#00b259] rounded-full shadow-[0_0_8px_#00b259] font-black'></div>
                            {t.marketplace}
                        </h4>
                        <ul className='flex flex-col gap-4 text-sm'>
                            {translatedMarketplace.map(link => (
                                <li key={link} className='hover:text-white hover:translate-x-2 cursor-pointer transition-all duration-300 flex items-center gap-2 group'>
                                    <span className='w-0 group-hover:w-4 h-0.5 bg-[#00b259] transition-all overflow-hidden'></span>
                                    {link}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 2: Quick Links */}
                    <div className='flex flex-col gap-8'>
                        <h4 className='text-white font-black text-sm uppercase tracking-[3px] flex items-center gap-3'>
                            <div className='w-2 h-2 bg-[#00b259] rounded-full shadow-[0_0_8px_#00b259] font-black'></div>
                            {t.quickLinks}
                        </h4>
                        <ul className='flex flex-col gap-4 text-sm'>
                            {translatedQuickLinks.map(link => (
                                <li key={link} className='hover:text-white hover:translate-x-2 cursor-pointer transition-all duration-300 flex items-center gap-2 group'>
                                    <span className='w-0 group-hover:w-4 h-0.5 bg-[#00b259] transition-all overflow-hidden'></span>
                                    {link}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 3: Farmers Hub */}
                    <div className='flex flex-col gap-8 text-sm'>
                        <h4 className='text-white font-black text-sm uppercase tracking-[3px] flex items-center gap-3'>
                            <div className='w-2 h-2 bg-[#00b259] rounded-full shadow-[0_0_8px_#00b259] font-black'></div>
                            {t.farmersHub}
                        </h4>
                        <ul className='flex flex-col gap-4'>
                            {translatedFarmersHub.map(link => (
                                <li key={link} className='hover:text-white hover:translate-x-2 cursor-pointer transition-all duration-300 flex items-center gap-2 group'>
                                    <span className='w-0 group-hover:w-4 h-0.5 bg-[#00b259] transition-all overflow-hidden'></span>
                                    {link}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 4: Contact */}
                    <div className='flex flex-col gap-8'>
                        <h4 className='text-white font-black text-sm uppercase tracking-[3px] flex items-center gap-3'>
                            <div className='w-2 h-2 bg-[#f39200] rounded-full shadow-[0_0_8px_#f39200] font-black'></div>
                            {t.getInTouch}
                        </h4>
                        <div className='flex flex-col gap-6'>
                            <a
                                href='tel:+916266761336'
                                onClick={() => trackContact({ method: 'Phone', location: 'Footer Call Support' })}
                                className='flex gap-4 items-start group cursor-pointer'
                            >
                                <div className='bg-black/20 p-3 rounded-2xl group-hover:bg-[#00b259]/20 transition-all border border-white/5'>
                                    <FaPhoneAlt className='text-[#00b259]' size={14} />
                                </div>
                                <div>
                                    <p className='text-[10px] text-gray-500 font-bold uppercase tracking-widest'>{t.callSupport}</p>
                                    <p className='text-white font-bold text-sm'>+91 6266761336</p>
                                </div>
                            </a>

                            <a
                                href={whatsappLink}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={() => trackContact({ method: 'WhatsApp', location: 'Footer WhatsApp Us' })}
                                className='flex gap-4 items-start group cursor-pointer'
                            >
                                <div className='bg-black/20 p-3 rounded-2xl group-hover:bg-[#25D366]/20 transition-all border border-white/5'>
                                    <FaWhatsapp className='text-[#25D366]' size={16} />
                                </div>
                                <div>
                                    <p className='text-[10px] text-gray-500 font-bold uppercase tracking-widest'>{t.whatsappUs}</p>
                                    <p className='text-white font-bold text-sm'>+91 6266761336</p>
                                </div>
                            </a>

                            <a
                                href='mailto:Contact@fasalshop.com'
                                onClick={() => trackContact({ method: 'Email', location: 'Footer Email Us' })}
                                className='flex gap-4 items-start group cursor-pointer'
                            >
                                <div className='bg-black/20 p-3 rounded-2xl group-hover:bg-[#00b259]/20 transition-all border border-white/5'>
                                    <FaEnvelope className='text-[#00b259]' size={14} />
                                </div>
                                <div>
                                    <p className='text-[10px] text-gray-500 font-bold uppercase tracking-widest'>Email Us</p>
                                    <p className='text-white font-bold text-sm'>Contact@fasalshop.com</p>
                                </div>
                            </a>

                            <div className='flex gap-4 items-start group cursor-pointer'>
                                <div className='bg-black/20 p-3 rounded-2xl group-hover:bg-[#f39200]/20 transition-all border border-white/5'>
                                    <FaMapMarkerAlt className='text-[#f39200]' size={16} />
                                </div>
                                <div>
                                    <p className='text-[10px] text-gray-500 font-bold uppercase tracking-widest'>{t.ourOffice}</p>
                                    <p className='text-white font-bold text-[12px] leading-tight'>{t.officeAddress}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* BOTTOM SECTION: SOCIAL & COPYRIGHT */}
                <div className='mt-20 pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8'>
                    <p className='text-[12px] font-bold text-gray-600 tracking-wider uppercase'>
                        © {new Date().getFullYear()} {t.copyright}
                    </p>

                    <div className='flex gap-3'>
                        {[
                            { icon: <FaWhatsapp />, color: 'hover:bg-[#25D366]', href: whatsappLink, label: 'WhatsApp' },
                            { icon: <FaInstagram />, color: 'hover:bg-gradient-to-tr hover:from-purple-600 hover:to-pink-500', href: 'https://instagram.com', label: 'Instagram' },
                            { icon: <FaFacebookF />, color: 'hover:bg-blue-600', href: 'https://facebook.com', label: 'Facebook' },
                            // { icon: <FaYoutube />, color: 'hover:bg-red-600', href: 'https://youtube.com', label: 'YouTube' },
                            { icon: <FaXTwitter />, color: 'hover:bg-black', href: 'https://x.com', label: 'Twitter' },
                            { icon: <FaLinkedinIn />, color: 'hover:bg-blue-700', href: 'https://linkedin.com', label: 'LinkedIn' }
                        ].map((social, idx) => (
                            <a
                                key={idx}
                                href={social.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                title={social.label}
                                onClick={() => {
                                    if (social.label === 'WhatsApp') {
                                        trackContact({ method: 'WhatsApp', location: 'Footer Social Icon' });
                                    }
                                }}
                                className={`w-10 h-10 rounded-xl bg-black/20 border border-white/5 flex items-center justify-center text-white cursor-pointer transition-all duration-300 ${social.color} hover:shadow-2xl hover:-translate-y-1.5`}
                            >
                                {React.cloneElement(social.icon, { size: 18 })}
                            </a>
                        ))}
                    </div>
                </div>

            </div>
        </footer>
    )
}

export default Footer